import { useEffect, useState, useCallback, Suspense, lazy, useRef } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { DashboardCards } from "@/components/dashboard/DashboardCards";
import { ShareLinksCard } from "@/components/dashboard/ShareLinksCard";
import { RevenueBreakdown } from "@/components/dashboard/RevenueBreakdown";
import { UpgradeCard } from "@/components/dashboard/UpgradeCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlusCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { ServiceType } from "@/types/booking";
import { Card } from "@/components/ui/card";
import { BookingsTableSkeleton, RevenueBreakdownSkeleton } from "@/components/ui/skeleton-block";
import { useBusinessProfile } from "@/hooks/useMagicBusinessProfile";
import { BusinessSignupData } from "@/components/onboarding/magic-steps/BusinessSignup";
import { DashboardActivationBanners } from "@/components/dashboard/DashboardActivationBanners";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { BusinessAnalytics } from '@/lib/posthog';
import { debugLog } from '@/lib/debug-logger';
import { supabase } from "@/integrations/supabase/client";

const RecentBookings = lazy(() => import("@/components/dashboard/RecentBookings").then(module => ({ default: module.RecentBookings })));
const UpcomingAppointments = lazy(() => import("@/components/dashboard/UpcomingAppointments").then(module => ({ default: module.UpcomingAppointments })));
const WalkInForm = lazy(() => import("@/components/dashboard/WalkInForm").then(module => ({ default: module.WalkInForm })));
const BookingCard = lazy(() => import("@/components/dashboard/BookingCard").then(module => ({ default: module.BookingCard })));
const NewBookingDialog = lazy(() => import("@/components/dashboard/NewBookingDialog").then(module => ({ default: module.NewBookingDialog })));

const RecentBookingsPlaceholder = () => <BookingsTableSkeleton rows={4} />;
const RevenuePlaceholder = () => <RevenueBreakdownSkeleton />;

type BookingStatus = "confirmed" | "completed" | "cancelled";

type Booking = {
  id: string;
  customerName: string;
  service: string;
  date: Date;
  time: string;
  price: number;
  status: BookingStatus;
  paymentMethod: "in_person" | "credit_card";
  qrCodeUrl?: string;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [walkInDialogOpen, setWalkInDialogOpen] = useState(false);
  const [newBookingDialogOpen, setNewBookingDialogOpen] = useState(false);
  const [services, setServices] = useState<ServiceType[]>([]);
  const [businessProfile, setBusinessProfile] = useState<BusinessSignupData | null>(null);
  const [hoursConfigured, setHoursConfigured] = useState<boolean | null>(null);
  const { loadBusinessProfile, isLoading: profileLoading } = useBusinessProfile();
  
  // Prevent duplicate profile loads
  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  
  // Load business profile and services on component mount - ONCE
  useEffect(() => {
    debugLog.dashboard('useEffect fired - hasLoadedRef:', hasLoadedRef.current, 'isLoadingRef:', isLoadingRef.current);
    // Skip if already loaded or currently loading
    if (hasLoadedRef.current || isLoadingRef.current) {
      debugLog.dashboard('Skipping load - already loaded or loading');
      return;
    }
    
    const fromOnboarding = location.state?.fromOnboarding;
    debugLog.dashboard('fromOnboarding:', fromOnboarding);
    
    const loadData = async () => {
      debugLog.dashboard('loadData START');
      isLoadingRef.current = true;
      
      try {
        debugLog.dashboard('Calling loadBusinessProfile()');
        const profile = await loadBusinessProfile();
        debugLog.dashboard('loadBusinessProfile returned - hasProfile:', !!profile);
        
        if (profile) {
          debugLog.dashboard('Profile loaded successfully');
          hasLoadedRef.current = true;
          setBusinessProfile(profile);
          debugLog.dashboard('hasLoadedRef set to true');

          // Track dashboard view
          if ((profile as any).businessId) {
            debugLog.dashboard('Tracking dashboard view for businessId:', (profile as any).businessId);
            BusinessAnalytics.dashboardViewed((profile as any).businessId);

            // Check if hours have been configured
            const { count } = await supabase
              .from('business_hours')
              .select('id', { count: 'exact', head: true })
              .eq('business_id', (profile as any).businessId);
            setHoursConfigured((count ?? 0) > 0);
          }
          
          // Convert business profile services to ServiceType format
          debugLog.dashboard('Converting services, count:', profile.services.length);
          const convertedServices = profile.services.map(service => ({
            id: service.id || Math.random().toString(),
            name: service.name,
            description: service.description,
            duration: service.duration,
            price: service.price,
          }));
          setServices(convertedServices);
        } else {
          debugLog.dashboard('Profile is null - checking if fromOnboarding');
          // Profile is null - only redirect if we're confident user needs onboarding
          // Give a short delay for newly onboarded users
          if (fromOnboarding) {
            // Coming from onboarding - wait a bit for DB propagation
            debugLog.dashboard('Coming from onboarding, waiting for profile...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            debugLog.dashboard('Retrying loadBusinessProfile()');
            const retryProfile = await loadBusinessProfile();
            debugLog.dashboard('Retry loadBusinessProfile returned - hasProfile:', !!retryProfile);
            if (retryProfile) {
              hasLoadedRef.current = true;
              setBusinessProfile(retryProfile);
              debugLog.dashboard('Retry successful, hasLoadedRef set to true');
              if ((retryProfile as any).businessId) {
                const { count } = await supabase
                  .from('business_hours')
                  .select('id', { count: 'exact', head: true })
                  .eq('business_id', (retryProfile as any).businessId);
                setHoursConfigured((count ?? 0) > 0);
              }
              const convertedServices = retryProfile.services.map(service => ({
                id: service.id || Math.random().toString(),
                name: service.name,
                description: service.description,
                duration: service.duration,
                price: service.price,
              }));
              setServices(convertedServices);
              return;
            }
          }
          
          // No business profile found - redirect to onboarding
          debugLog.dashboard('No business profile found, REDIRECTING to /magic-onboarding-v2');
          navigate('/magic-onboarding-v2');
        }
      } catch (error) {
        console.error('[Dashboard] Error loading business profile:', error);
        // Don't redirect on error - could be a network issue
        // User can refresh the page
        toast.error('Failed to load business profile. Please refresh the page.');
      } finally {
        debugLog.dashboard('loadData FINALLY - setting isLoadingRef to false');
        isLoadingRef.current = false;
      }
    };
    
    debugLog.dashboard('Calling loadData()');
    loadData();
  }, [loadBusinessProfile, navigate, location.state]);
  
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  
  const handleWalkInCreated = useCallback((walkIn: any) => {
    console.log("Walk-in created:", walkIn);
    setWalkInDialogOpen(false);
    toast.success(`${walkIn.customerName} added to walk-in queue`);
    
    const newBooking: Booking = {
      id: `walk-in-${Date.now()}`,
      customerName: walkIn.customerName,
      service: walkIn.service,
      date: new Date(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      price: services.find(s => s.name === walkIn.service)?.price || 0,
      status: "confirmed",
      paymentMethod: "in_person",
      qrCodeUrl: `https://api.stripe.com/v1/payment_links/plink_1NpEQIJwXEcQ7JasFGUvj5Bj/qr_codes/pqrc_1NpEQIJwXEcQ7JasDQ7dnV4y`
    };
    
    setRecentBookings(prev => [newBooking, ...prev]);
  }, [services]);
  
  const handleCancelBooking = useCallback((id: string) => {
    setRecentBookings(prev => prev.map(booking => 
      booking.id === id 
        ? {...booking, status: "cancelled" as BookingStatus}
        : booking
    ));
    toast.success("Booking canceled successfully");
  }, []);
  
  const handleCompleteBooking = useCallback((id: string) => {
    setRecentBookings(prev => prev.map(booking => 
      booking.id === id 
        ? {...booking, status: "completed" as BookingStatus} 
        : booking
    ));
    toast.success("Booking completed successfully");
  }, []);

  useEffect(() => {
    document.title = "Dashboard | Figgy.link";
    
    const prefetchTimer = setTimeout(() => {
      import("@/components/dashboard/RecentBookings");
      import("@/components/dashboard/UpcomingAppointments");
    }, 500);
    
    return () => clearTimeout(prefetchTimer);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-full lg:max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">Dashboard</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Welcome back! Here's an overview of your business.
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-2 sm:space-x-3">
              <Button 
                variant="outline"
                onClick={() => setWalkInDialogOpen(true)}
                className="flex items-center text-xs sm:text-sm"
                size="sm"
              >
                <UserPlus className="mr-1 h-4 w-4" />
                Add Walk-in
              </Button>
              <Button
                className="bg-figgy-500 hover:bg-figgy-600 text-xs sm:text-sm"
                size="sm"
                onClick={() => setNewBookingDialogOpen(true)}
              >
                <PlusCircle className="mr-1 h-4 w-4" />
                New Booking
              </Button>
            </div>
          </div>
          
          <div className="space-y-5">
            <DashboardActivationBanners />

            {hoursConfigured === false && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <span>
                  ⚠️ Your booking page is not live yet — until you save your availability hours, customers will see no bookable times.
                </span>
                <Link
                  to="/dashboard/hours"
                  className="shrink-0 font-medium underline underline-offset-2 hover:text-amber-900"
                >
                  Set your hours →
                </Link>
              </div>
            )}

            <DashboardCards />

            {/* Share Links Section */}
            <ShareLinksCard bookingUrl={businessProfile?.bookingUrl} />

            {/* Upgrade Card (only shows for Free users with meaningful activity) */}
            <UpgradeCard />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="lg:col-span-2 content-visibility-auto" style={{ containIntrinsicSize: '0 600px' }}>
                <Suspense fallback={<RecentBookingsPlaceholder />}>
                  <RecentBookings />
                </Suspense>
              </div>
              <div className="space-y-4">
                <RevenueBreakdown />
                <div className="content-visibility-auto" style={{ containIntrinsicSize: '0 400px' }}>
                  <Suspense fallback={<RevenuePlaceholder />}>
                    <UpcomingAppointments />
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Dialog open={walkInDialogOpen} onOpenChange={setWalkInDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Walk-in Customer</DialogTitle>
          </DialogHeader>
          <Suspense fallback={
            <div className="space-y-4 p-1" aria-hidden="true">
              <div className="space-y-2">
                <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-20" />
                <div className="motion-safe:animate-pulse bg-muted rounded h-10 w-full" />
              </div>
              <div className="space-y-2">
                <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-24" />
                <div className="motion-safe:animate-pulse bg-muted rounded h-10 w-full" />
              </div>
              <div className="motion-safe:animate-pulse bg-muted rounded h-10 w-full mt-2" />
            </div>
          }>
            <WalkInForm services={services} onWalkInCreated={handleWalkInCreated} />
          </Suspense>
        </DialogContent>
      </Dialog>

      <Suspense fallback={null}>
        <NewBookingDialog
          open={newBookingDialogOpen}
          onOpenChange={setNewBookingDialogOpen}
          onBookingCreated={() => {
            // Refresh the page or reload bookings
            toast.success("Booking created! Refreshing...");
            setTimeout(() => window.location.reload(), 1000);
          }}
        />
      </Suspense>
    </div>
  );
};

export default Dashboard;
